import * as React from "react";
import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import MealPlannerPage from "./pages/MealPlannerPage";
import { JoinPage } from "./pages/JoinPage";
import { Layout } from "./Layout";
import { AxiosError } from "axios";
import {
  keycloak,
  KeycloakContext,
  KeycloakProvider,
  LoadingContext,
  setLoadingFunction,
} from "./Keycloak";
import StackTrace from "stacktrace-js";
import { ToastProvider } from "./components/ui/Toast";
import { ColorModeProvider } from "./ColorMode";
import { reportError } from "./api";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { QueryBoundary } from "./components/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import("@tanstack/query-core").QueryClient;
  }
}

window.__TANSTACK_QUERY_CLIENT__ = queryClient;

function isAxiosError(error: Error): error is AxiosError {
  return error.name == "AxiosError";
}

// Schlägt das Fehler-Reporting fehl, darf es keinen neuen
// `unhandledrejection` auslösen (sonst Endlosschleife).
let reportingError = false;
const postError = (msg: string) => {
  if (reportingError) return;
  reportingError = true;
  reportError({ body: { error: msg } })
    .catch(() => undefined)
    .finally(() => {
      reportingError = false;
    });
};

export const App = () => {
  useEffect(() => {
    const handleGlobalError = async (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error,
    ): Promise<boolean> => {
      if (error) {
        const stackframes = await StackTrace.fromError(error);
        const stackString = stackframes.map((sf) => sf.toString()).join("\n");
        postError(`JS-Error (${error.name}):${error.message}\n${stackString}`);
      } else {
        const errorInfo = JSON.stringify({
          message,
          source,
          lineno,
          colno,
          error,
        });
        postError(`JS-Error (unknown):\n${errorInfo}`);
      }
      return true;
    };

    const handleUnhandledRejection = async (
      event: PromiseRejectionEvent,
    ): Promise<void> => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      const stackframes = await StackTrace.fromError(error);
      const stackString = stackframes.map((sf) => sf.toString()).join("\n");

      if (isAxiosError(error) && error.response?.status == 401 && keycloak) {
        const { token, idToken, refreshToken } = keycloak;
        const tokens = JSON.stringify({
          token,
          idToken,
          refreshToken,
        });
        postError(`Auth Error: ${tokens}`);

        keycloak.login({
          redirectUri: window.location.href,
        });
      } else {
        postError(`Unhandled Rejection:\n${stackString}`);
      }
    };

    window.onerror = handleGlobalError;
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.onerror = null;
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);

  const [loading, setLoading] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(true);
  useEffect(() => {
    setLoadingFunction(setLoading);
  }, [setLoading]);

  const pageContent = (
    <LoadingContext.Provider
      value={{ loading, setLoading, setShowLoading, showLoading }}
    >
      <BrowserRouter>
        <Layout>
          <QueryBoundary>
            <Routes>
              <Route path="/" element={<MealPlannerPage />} />
              <Route path="/plan" element={<MealPlannerPage />} />
              <Route path="/join" element={<JoinPage />} />
            </Routes>
          </QueryBoundary>
        </Layout>
      </BrowserRouter>
    </LoadingContext.Provider>
  );
  const useKeycloak = !import.meta.env.VITE_DISABLE_KEYCLOAK;
  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeProvider>
        <ToastProvider />
        {useKeycloak ? (
          <KeycloakProvider>
            <KeycloakContext.Consumer>
              {({ keycloak, authenticated }) =>
                !keycloak ? (
                  <div>Loading...</div>
                ) : !authenticated ? (
                  <div>
                    Unauthorized
                    <button
                      onClick={() => {
                        window.location.reload();
                      }}
                    >
                      Seite neu laden
                    </button>
                  </div>
                ) : (
                  pageContent
                )
              }
            </KeycloakContext.Consumer>
          </KeycloakProvider>
        ) : (
          pageContent
        )}
      </ColorModeProvider>
    </QueryClientProvider>
  );
};
